import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
    ContractOptionsActionTypes, DeleteContractOptionsResponse,
    GetContractOptionsResponse, PatchContractOptionsResponse,
    PostContractOptionsResponse,
} from '../../actions/mdqx/contract-options.actions';
import { ContractOptionsEntity } from '../../../entities/mdqx/contract-options.entity';
import { getToken } from '../../selectors/base.selectors';
import { stringifyIfNotNullOrUndefined } from '../../../../helper/helper';
import { HttpService } from '../../../services/http.service';
import { DaveActions } from '../../actions/actions';
import { BaseActionTypes } from '../../actions/base.actions';
import { State } from '../../index';
import { environment } from '../../../../../environments/environment';
enum ErrorCodes {
    Get = 'Vertragsoptionen Abrufen fehlgeschlagen',
    Post = 'Vertragsoptionen Anlegen fehlgeschlagen',
    Patch = 'Vertragsoptionen Bearbeiten fehlgeschlagen',
    Delete = 'Vertragsoptionen Löschen fehlgeschlagen',
}

@Injectable()
export class ContractOptionsEffects {
    
    get$ = createEffect(() => this.actions$.pipe(
        ofType(ContractOptionsActionTypes.GetContractOptionsRequest),
        map(
            () => `
            {
                contractoptions {
                    ${ContractOptionsEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        switchMap(([query, token]) =>
            this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                map(res => res && res.contractoptions),
                concatMap(res =>
                    res
                        ? [
                              new GetContractOptionsResponse(
                                  res.map(
                                      contractOption => new ContractOptionsEntity(contractOption),
                                  ),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Get,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    post$ = createEffect(() => this.actions$.pipe(
        ofType(ContractOptionsActionTypes.PostContractOptionsRequest),
        map(
            ({ Payload }) => `
            mutation {
                createContractoptions(
                    ${stringifyIfNotNullOrUndefined(Payload, 'casenumber')}
                    contractId: ${JSON.stringify(Payload.contractId)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'dtaOptions')}
                    ${stringifyIfNotNullOrUndefined(
                        Payload,
                        'einreichfristNachQuartalEndeInWochen',
                    )}
                    ${stringifyIfNotNullOrUndefined(Payload, 'hatAnamnese')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'istWkv')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'kuendigungsfristInTagen')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'laufzeitInMonaten')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'medizinprodukteEigenleistungErlaubt')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'mindestalterVersicherter')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'teAnzahlSeiten')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'teGueltigkeitId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'widerrufsfristInTagen')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'teGueltigkeitDauerUnit')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'teGueltigkeitDauerValue')}
                ) {
                    ${ContractOptionsEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                map(res => res && res.createContractoptions),
                concatMap(res =>
                    res
                        ? [new PostContractOptionsResponse(new ContractOptionsEntity(res))]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Post,
                        Err: err,
                        Caught: caught,
                    }
                }))),            ),
        ),
    ));

    
    patch$ = createEffect(() => this.actions$.pipe(
        ofType(ContractOptionsActionTypes.PatchContractOptionsRequest),
        map(
            ({ Payload }) => `
            mutation {
                changeContractoptions(
                    ${stringifyIfNotNullOrUndefined(Payload, 'casenumber')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'dtaOptions')}
                    ${stringifyIfNotNullOrUndefined(
                        Payload,
                        'einreichfristNachQuartalEndeInWochen',
                    )}
                    ${stringifyIfNotNullOrUndefined(Payload, 'hatAnamnese')}
                    id: ${JSON.stringify(Payload.id)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'istWkv')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'kuendigungsfristInTagen')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'laufzeitInMonaten')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'medizinprodukteEigenleistungErlaubt')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'mindestalterVersicherter')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'teAnzahlSeiten')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'teGueltigkeitId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'widerrufsfristInTagen')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'teGueltigkeitDauerUnit')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'teGueltigkeitDauerValue')}
                ) {
                    ${ContractOptionsEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                map(res => res && res.changeContractoptions),
                concatMap(res =>
                    res
                        ? [new PatchContractOptionsResponse(new ContractOptionsEntity(res))]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Patch,
                        Err: err,
                        Caught: caught,
                    }
                }))),
                ),
        ),
    ));

    
    delete$ = createEffect(() => this.actions$.pipe(
        ofType(ContractOptionsActionTypes.DeleteContractOptionsRequest),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([{ Payload }, token]) =>
            this.api
                .graphQl(
                    {
                        query: `
                        mutation {
                            deleteContractoptions(id: ${JSON.stringify(Payload.id)})
                        }`,
                    },
                    { token }, '', environment.mdqxGatewayApiUrl
                )
                .pipe(
                    map(res => res && res.deleteContractoptions),
                    concatMap(res =>
                        res
                            ? [new DeleteContractOptionsResponse(Payload.id)]
                            : throwError('falsy response'),
                    ),
                    catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Delete,
                            Err: err,
                            Caught: caught,
                        }
                    }))),
                    ),
        ),
    ));

    constructor(
        private actions$: Actions<DaveActions>,
        private api: HttpService,
        private store: Store<State>,
    ) {}
}
